import { useQuery } from "@apollo/client";
import { Box } from "@material-ui/core";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";

import { useCurrentPlatform, useCurrentUser } from "@/contexts";
import { useScopedNavigate, useScopeRelativePathname } from "@/hooks";
import { useT } from "@/i18n";
import { DisciplineTable } from "src/components/dashboard/disciplines";
import NotFoundComp from "src/components/dashboard/NotFound";
import Loading from "src/components/Loading";
import { DISCIPLINES } from "src/graphql/queries";
import useFederationRedirect from "src/utils/redirectTodashboard";

const Disciplines: FC = () => {
  const navigate = useScopedNavigate();
  const t = useT();
  const pathname = useScopeRelativePathname();
  const [disciplineData, setDisciplineData] = useState<any>([]);
  const currentPlatform = useCurrentPlatform();
  const currentUser = useCurrentUser();
  const { checkAndRedirect } = useFederationRedirect();
  const { loading, refetch } = useQuery(DISCIPLINES, {
    variables: {
      filter: {
        ...(pathname === "/platform-disciplines" && {
          platform: currentPlatform.id,
        }),
      },
    },
    onCompleted: (data) => {
      setDisciplineData(data?.disciplines);
    },
  });

  useEffect(() => {
    refetch();
  }, [currentPlatform.id, refetch]);

  useEffect(() => {
    checkAndRedirect(currentPlatform.id, currentUser);
  }, [currentPlatform.id, currentUser, checkAndRedirect]);

  const refDisplay = useRef<HTMLDivElement>(null);
  const handleScroll = () => {
    setTimeout(() => {
      if (refDisplay && refDisplay.current) {
        refDisplay.current.scrollIntoView({
          block: "start",
        });
      }
    }, 0);
  };

  const { IsSuperAdmin } = useFederationRedirect();

  useEffect(() => {
    IsSuperAdmin();
    handleScroll();
  }, [IsSuperAdmin]);

  if (loading) {
    return <Loading />;
  }
  return (
    <>
      <Helmet>
        <title>Dashboard: Disciplines</title>
      </Helmet>
      <Box ref={refDisplay} />
      <Box
        sx={{
          backgroundColor: "background.default",
          minHeight: "calc(100% - 110px)",
          border: "1px solid rgb(237, 241, 246)",
          px: { sm: "24px", xs: "10px" },
          my: 4,
          pt: 2,
          borderRadius: "8px",
        }}
      >
        <Box sx={{ mt: 1 }}>
          {disciplineData?.length > 0 ? (
            <DisciplineTable tour={disciplineData} />
          ) : (
            <>
              <Box>
                <NotFoundComp
                  link={() => navigate("/disciplines/new")}
                  btnTitle={currentUser.role !== "federation" ? t("ADD NEW DISCIPLINE") : undefined}
                  pageTitle="Disciplines"
                  noText={t("There is no available disciplines")}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Disciplines;
